import supabase from './supabaseClient';
import { TERMS_VERSION } from '../constants/termsOfService';
import { writeAuditLog } from './auditService';

const isSchemaMissingError = (error) => {
  if (!error) return false;
  return ['42P01', '42703', 'PGRST204', 'PGRST205'].includes(error.code);
};

export const getTermsRequirementForUser = async ({ organizationId, userId }) => {
  if (!organizationId || !userId) {
    return {
      required: false,
      termsVersion: TERMS_VERSION,
      latestAcceptanceAt: null,
      requiredSince: null,
    };
  }

  const { data: orgData, error: orgError } = await supabase
    .from('organizations')
    .select('terms_version_required, terms_reaccept_required_at')
    .eq('id', organizationId)
    .single();

  if (orgError) {
    if (isSchemaMissingError(orgError)) {
      return {
        required: false,
        termsVersion: TERMS_VERSION,
        latestAcceptanceAt: null,
        requiredSince: null,
      };
    }
    throw orgError;
  }

  const termsVersion = orgData?.terms_version_required || TERMS_VERSION;
  const requiredSince = orgData?.terms_reaccept_required_at || null;

  const { data: acceptanceRows, error: acceptanceError } = await supabase
    .from('organization_terms_acceptances')
    .select('accepted_at')
    .eq('organization_id', organizationId)
    .eq('user_id', userId)
    .eq('terms_version', termsVersion)
    .order('accepted_at', { ascending: false })
    .limit(1);

  if (acceptanceError) {
    if (isSchemaMissingError(acceptanceError)) {
      return {
        required: false,
        termsVersion,
        latestAcceptanceAt: null,
        requiredSince,
      };
    }
    throw acceptanceError;
  }

  const latestAcceptanceAt = acceptanceRows?.[0]?.accepted_at || null;
  const reacceptNeeded =
    Boolean(requiredSince) &&
    (!latestAcceptanceAt || new Date(latestAcceptanceAt).getTime() < new Date(requiredSince).getTime());
  const required = !latestAcceptanceAt || reacceptNeeded;

  return {
    required,
    termsVersion,
    latestAcceptanceAt,
    requiredSince,
  };
};

export const acceptTermsForUser = async ({
  organizationId,
  userId,
  termsVersion,
  acceptedByName,
  acceptedByEmail,
  organizationName,
}) => {
  const payload = {
    organization_id: organizationId,
    user_id: userId,
    terms_version: termsVersion || TERMS_VERSION,
    accepted_at: new Date().toISOString(),
    accepted_by_name: acceptedByName || null,
    accepted_by_email: acceptedByEmail || null,
  };

  const { data, error } = await supabase
    .from('organization_terms_acceptances')
    .insert(payload)
    .select()
    .single();

  if (error) throw error;

  // Also write to main audit_logs for cross-app audit trail
  await writeAuditLog({
    sourceTable: 'organization_terms_acceptances',
    entityType: 'terms_enforcement',
    entityId: organizationId,
    action: 'accepted',
    summary: `User accepted Terms of Service (v${termsVersion || TERMS_VERSION}) for "${organizationName || organizationId}"`,
    notes: `User: ${acceptedByName || acceptedByEmail || userId} | Version: ${termsVersion || TERMS_VERSION}`,
    afterData: { termsVersion, organizationId, organizationName, acceptedByName, acceptedByEmail },
    actorUserId: userId,
    actorName: acceptedByName,
    actorRole: 'User',
    changedAt: payload.accepted_at,
  });

  return data;
};

export const revokeTermsAcceptancesForOrganization = async ({ organizationId }) => {
  if (!organizationId) {
    throw new Error('organizationId is required');
  }

  const { error } = await supabase
    .from('organization_terms_acceptances')
    .delete()
    .eq('organization_id', organizationId);

  if (error) throw error;
  return true;
};

export const getTermsAcceptanceHistoryForOrganization = async ({ organizationId, limit = 50 }) => {
  if (!organizationId) {
    throw new Error('organizationId is required');
  }

  const { data, error } = await supabase
    .from('organization_terms_acceptances')
    .select('id, user_id, terms_version, accepted_at, accepted_by_name, accepted_by_email')
    .eq('organization_id', organizationId)
    .order('accepted_at', { ascending: false })
    .limit(limit);

  if (error) {
    if (isSchemaMissingError(error)) return [];
    throw error;
  }

  return data || [];
};

export const logTermsAdminAction = async ({
  organizationId,
  actionType,
  termsVersion,
  actedByUserId,
  actedByName,
  actedByEmail,
  organizationName,
  metadata,
}) => {
  const actionLabel = actionType === 'require_next_login' ? 'Required Terms at Next Login' : 'Revoked + Re-required Terms';
  const auditSummary = `${actionLabel} for "${organizationName || organizationId}" (Terms v${termsVersion || TERMS_VERSION})`;

  // Always write to main audit_logs so it shows in the Audit Logs page
  await writeAuditLog({
    sourceTable: 'organization_terms_acceptances',
    entityType: 'terms_enforcement',
    entityId: organizationId,
    action: actionType,
    summary: auditSummary,
    notes: `Admin: ${actedByName || actedByEmail || actedByUserId} | Org: ${organizationName || organizationId} | Version: ${termsVersion || TERMS_VERSION}`,
    afterData: { actionType, termsVersion, organizationId, organizationName },
    actorUserId: actedByUserId,
    actorName: actedByName,
    actorRole: 'Super Admin',
    changedAt: new Date().toISOString(),
  });

  // Also attempt to write to the dedicated terms admin audit table if it exists
  const payload = {
    organization_id: organizationId,
    action_type: actionType,
    terms_version: termsVersion || TERMS_VERSION,
    acted_by_user_id: actedByUserId,
    acted_by_name: actedByName || null,
    acted_by_email: actedByEmail || null,
    metadata: metadata || {},
  };

  const { data, error } = await supabase
    .from('organization_terms_admin_actions')
    .insert(payload)
    .select()
    .single();

  if (error) {
    // Keep core flows working even if the audit table migration has not been applied yet.
    if (isSchemaMissingError(error)) {
      console.warn('Terms admin audit table missing in schema cache. Skipping admin action log.', error);
      return null;
    }
    throw error;
  }
  return data;
};

export const getTermsAuditFeed = async ({ organizationIds = [], limit = 300 }) => {
  let acceptancesQuery = supabase
    .from('organization_terms_acceptances')
    .select('id, organization_id, user_id, terms_version, accepted_at, accepted_by_name, accepted_by_email')
    .order('accepted_at', { ascending: false })
    .limit(limit);

  let adminActionsQuery = supabase
    .from('organization_terms_admin_actions')
    .select('id, organization_id, action_type, terms_version, acted_at, acted_by_user_id, acted_by_name, acted_by_email, metadata')
    .order('acted_at', { ascending: false })
    .limit(limit);

  // Fallback: query audit_logs for admin actions when dedicated table is unavailable
  let auditLogsFallbackQuery = supabase
    .from('audit_logs')
    .select('id, entity_id, action, summary, notes, actor_user_id, actor_name, changed_at')
    .eq('entity_type', 'terms_enforcement')
    .in('action', ['require_next_login', 'revoke_and_rerequire'])
    .order('changed_at', { ascending: false })
    .limit(limit);

  if (organizationIds.length > 0) {
    acceptancesQuery = acceptancesQuery.in('organization_id', organizationIds);
    adminActionsQuery = adminActionsQuery.in('organization_id', organizationIds);
    auditLogsFallbackQuery = auditLogsFallbackQuery.in('entity_id', organizationIds);
  }

  const [
    { data: acceptanceRows, error: acceptanceError },
    { data: adminRows, error: adminError },
    { data: auditLogRows, error: auditLogError },
  ] = await Promise.all([acceptancesQuery, adminActionsQuery, auditLogsFallbackQuery]);

  if (acceptanceError && !isSchemaMissingError(acceptanceError)) throw acceptanceError;
  if (adminError && !isSchemaMissingError(adminError)) {
    // if dedicated table missing, we fall through to auditLogRows below
    if (!isSchemaMissingError(adminError)) throw adminError;
  }
  if (auditLogError && !isSchemaMissingError(auditLogError)) throw auditLogError;

  const acceptanceEvents = (acceptanceRows || []).map((row) => ({
    id: `accepted-${row.id}`,
    organizationId: row.organization_id,
    eventType: 'accepted',
    termsVersion: row.terms_version,
    happenedAt: row.accepted_at,
    actorUserId: row.user_id,
    actorName: row.accepted_by_name,
    actorEmail: row.accepted_by_email,
    metadata: {},
  }));

  // Use dedicated admin table if available, otherwise parse from audit_logs
  const useDedicatedTable = !adminError || !isSchemaMissingError(adminError);
  let adminEvents;

  if (useDedicatedTable && (adminRows || []).length > 0) {
    adminEvents = adminRows.map((row) => ({
      id: `${row.action_type}-${row.id}`,
      organizationId: row.organization_id,
      eventType: row.action_type,
      termsVersion: row.terms_version,
      happenedAt: row.acted_at,
      actorUserId: row.acted_by_user_id,
      actorName: row.acted_by_name,
      actorEmail: row.acted_by_email,
      metadata: row.metadata || {},
    }));
  } else {
    // Fallback: derive admin events from audit_logs
    adminEvents = (auditLogRows || []).map((row) => {
      // notes format: "Admin: <name> | Org: <name> | Version: <ver>"
      const noteParts = Object.fromEntries(
        (row.notes || '').split('|').map((p) => {
          const [k, ...v] = p.split(':');
          return [k.trim(), v.join(':').trim()];
        }),
      );
      return {
        id: `auditlog-${row.id}`,
        organizationId: row.entity_id,
        eventType: row.action,
        termsVersion: noteParts['Version'] || TERMS_VERSION,
        happenedAt: row.changed_at,
        actorUserId: row.actor_user_id,
        actorName: row.actor_name || noteParts['Admin'] || null,
        actorEmail: null,
        metadata: {},
        _source: 'audit_logs_fallback',
      };
    });
  }

  return [...acceptanceEvents, ...adminEvents]
    .sort((a, b) => new Date(b.happenedAt).getTime() - new Date(a.happenedAt).getTime())
    .slice(0, limit);
};
